import ContentMarkdown, { type CitationResolver } from "../../ContentMarkdown";
import { ChatSourceIdentity, type ChatSource } from "../../ui/ChatSources";
import { CitationChip } from "../../ui/CitationChip";
import type { GptSourceRef } from "../../../domains/gpt/transcript";
import type { AssistantDataParts } from "../../../../../api/src/shared/assistant-ui-message";
import {
  LayoutDebated,
  LayoutDifferential,
  LayoutDrugCard,
  LayoutEvidenceTable,
  LayoutKeyPoint,
  LayoutMechanismList,
  LayoutMissing,
  LayoutOrderedSteps,
  LayoutParagraph,
  LayoutRecommendation,
  LayoutReferenceCard,
  LayoutResearchQuestion,
  LayoutSection,
  LayoutStudyCard,
  LayoutSummary,
} from "./LayoutComponents";

type Layout = AssistantDataParts["layout"];
type Component = Exclude<Layout["components"][number], { type: "Section" }>;
type LayoutText = { block: number; text: string };

// Entry n of the answer's sources: its row of the Sources tab, and the sheet that row opens.
export type LayoutSource = {
  ref: GptSourceRef;
  shown: ChatSource;
  number: number;
  onOpen: () => void;
};

// A text sits in its component's line: its paragraph renders inline.
const LAYOUT_TEXT_CLASS =
  "inline [&_p]:inline [&_p]:my-0 [&_strong]:font-semibold";

function recommendationOrigin(
  ref: GptSourceRef
): { kind: "guideline"; year: number | null } | { kind: "reported" } {
  switch (ref.provenance) {
    case "pubmed":
    case "web":
      return ref.studyDesign !== null &&
        ref.studyDesign.articleType === "Guidelines / Consensus"
        ? { kind: "guideline", year: ref.year }
        : { kind: "reported" };
    case "juisci_internal":
    case "uploaded_document":
      return { kind: "reported" };
    default:
      throw new Error(ref satisfies never);
  }
}

/**
 * A published answer composed for its task, in the components of the genUI
 * catalog. Each text is the Markdown of the answer's text: its citations are
 * the chips of the Markdown answer, numbered and opened in the same way.
 */
export function AnswerLayout({
  layout,
  math,
  citation,
  source,
}: {
  layout: Layout;
  math: boolean;
  citation: CitationResolver;
  source: (number: number) => LayoutSource;
}): React.JSX.Element {
  const { language } = layout;
  const text = (value: LayoutText) => (
    <ContentMarkdown
      className={LAYOUT_TEXT_CLASS}
      math={math}
      linkVariant="tag"
      streaming={false}
      citation={citation}
    >
      {value.text}
    </ContentMarkdown>
  );
  const optional = (value: LayoutText | null) =>
    value === null ? null : text(value);
  // A card's source line opens the source, as its row in the Sources tab does.
  const sourceLine = (number: number) => {
    const found = source(number);
    return (
      <button
        type="button"
        onClick={found.onOpen}
        className="flex w-full cursor-pointer items-start gap-2 border-none bg-transparent p-0 text-left"
      >
        <CitationChip number={found.number} size="md" />
        <ChatSourceIdentity source={found.shown} size="row" />
      </button>
    );
  };
  const sourceName = (number: number) => {
    const found = source(number);
    return (
      <button
        type="button"
        onClick={found.onOpen}
        className="flex cursor-pointer items-start gap-1.5 border-none bg-transparent p-0 text-left text-inherit"
      >
        <CitationChip number={found.number} size="sm" />
        <span>
          {found.shown.identity === ""
            ? found.shown.title
            : found.shown.identity}
        </span>
      </button>
    );
  };
  const render = (component: Component, key: number) => {
    switch (component.type) {
      case "Summary":
        return (
          <LayoutSummary
            key={key}
            points={component.points.map((point) => text(point))}
            language={language}
          />
        );
      case "Paragraph":
        return <LayoutParagraph key={key} text={text(component.text)} />;
      case "KeyPoint":
        return (
          <LayoutKeyPoint
            key={key}
            tone={component.tone}
            text={text(component.text)}
          />
        );
      case "StudyCard":
        return (
          <LayoutStudyCard
            key={key}
            source={sourceLine(component.sourceId)}
            takeaway={text(component.takeaway)}
          />
        );
      case "ReferenceCard":
        return (
          <LayoutReferenceCard
            key={key}
            source={sourceLine(component.sourceId)}
            population={optional(component.population)}
            endpoint={optional(component.endpoint)}
            result={text(component.result)}
            language={language}
          />
        );
      case "EvidenceTable":
        return (
          <LayoutEvidenceTable
            key={key}
            columns={component.columns}
            rows={component.rows.map((row) => ({
              label: row.label,
              source: row.sourceId === null ? null : sourceName(row.sourceId),
              cells: row.cells.map((cell) => text(cell)),
            }))}
          />
        );
      case "RecommendationBox":
        return (
          <LayoutRecommendation
            key={key}
            issuer={component.issuer}
            text={text(component.text)}
            source={sourceName(component.sourceId)}
            origin={recommendationOrigin(source(component.sourceId).ref)}
            language={language}
          />
        );
      case "Differential":
        return (
          <LayoutDifferential
            key={key}
            likely={component.likely.map((item) => ({
              name: item.name,
              text: text(item.detail),
            }))}
            dontMiss={component.dontMiss.map((item) => ({
              name: item.name,
              text: text(item.redFlags),
            }))}
            other={component.other.map((item) => ({
              name: item.name,
              text: text(item.detail),
            }))}
            language={language}
          />
        );
      case "OrderedSteps":
        return (
          <LayoutOrderedSteps
            key={key}
            steps={component.steps.map((step) => ({
              title: step.title,
              text: text(step.text),
              line: step.line,
            }))}
            language={language}
          />
        );
      case "MechanismList":
        return (
          <LayoutMechanismList
            key={key}
            items={component.items.map((item) => ({
              term: item.term,
              text: text(item.text),
            }))}
          />
        );
      case "DrugCard":
        return (
          <LayoutDrugCard
            key={key}
            name={component.name}
            indication={optional(component.indication)}
            dose={text(component.dose)}
            renal={optional(component.renal)}
            interactions={optional(component.interactions)}
            language={language}
          />
        );
      case "Debated":
        return (
          <LayoutDebated
            key={key}
            question={text(component.question)}
            positions={component.positions.map((position) => ({
              label: position.label,
              text: text(position.text),
            }))}
            language={language}
          />
        );
      case "ResearchQuestion":
        return (
          <LayoutResearchQuestion
            key={key}
            title={component.title}
            question={text(component.question)}
            population={text(component.population)}
            comparison={text(component.comparison)}
            outcome={text(component.outcome)}
            method={text(component.method)}
            language={language}
          />
        );
      default:
        throw new Error(component satisfies never);
    }
  };
  const rendered = layout.components.map((component, index) =>
    component.type === "Section" ? (
      <LayoutSection key={index} title={component.title}>
        {component.children.map((child, childIndex) =>
          render(child, childIndex)
        )}
      </LayoutSection>
    ) : (
      render(component, index)
    )
  );
  // The missing information follows the key points, as in the Markdown text.
  const [opening] = layout.components;
  const opensOnSummary = opening !== undefined && opening.type === "Summary";
  return (
    <div className="flex flex-col gap-4">
      {opensOnSummary ? rendered.slice(0, 1) : null}
      {layout.unresolvedQuestions.length === 0 ? null : (
        <LayoutMissing
          questions={layout.unresolvedQuestions}
          language={language}
        />
      )}
      {opensOnSummary ? rendered.slice(1) : rendered}
    </div>
  );
}
